import {
  Keyboard,
  LoaderCircle,
  Mic,
  RotateCcw,
  Send,
  Square,
  Volume2,
  VolumeX
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ConversationMessage } from "../types";
import { useVoiceCapture, type CaptureResult } from "../lib/useVoiceCapture";
import { TurnstileWidget } from "./TurnstileWidget";

function formatDuration(seconds: number) {
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export function CaptureView({
  busy,
  turnstileSiteKey,
  messages,
  speaking,
  autoStopOnSilence = true,
  onToggleSpeech,
  onProcess
}: {
  busy: boolean;
  turnstileSiteKey?: string;
  messages?: ConversationMessage[];
  speaking?: boolean;
  autoStopOnSilence?: boolean;
  onToggleSpeech?: () => void;
  onProcess: (transcript: string, audio?: Blob, turnstileToken?: string) => Promise<boolean>;
}) {
  const recorder = useVoiceCapture(undefined, autoStopOnSilence);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [processingSource, setProcessingSource] = useState<"voice" | "text">("text");
  const [turnstileToken, setTurnstileToken] = useState<string>();
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const conversationRef = useRef<HTMLDivElement>(null);
  const recording = recorder.status === "recording";
  const finishing = recorder.status === "requesting" || recorder.status === "finalizing";
  const ready = recorder.status === "ready";
  const previewUnavailable = recording && Boolean(recorder.error);
  const processingLabel =
    recorder.status === "requesting"
      ? "J’ouvre le microphone…"
      : recorder.status === "finalizing"
        ? "Je prépare votre vocal…"
        : processingSource === "voice"
          ? "Je transcris et comprends votre vocal…"
          : "Je comprends votre demande…";

  useEffect(() => {
    if (recorder.status === "error") setShowKeyboard(true);
  }, [recorder.status]);

  useEffect(() => {
    const conversation = conversationRef.current;
    if (!conversation) return;
    const scrollToLatest = () => {
      conversation.scrollTop = conversation.scrollHeight;
    };
    scrollToLatest();
    const frame = window.requestAnimationFrame(scrollToLatest);
    const observer = new ResizeObserver(scrollToLatest);
    observer.observe(conversation);
    Array.from(conversation.children).forEach((child) => observer.observe(child));
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [busy, messages]);

  const send = async (override?: CaptureResult) => {
    const text = override ? override.transcript : recorder.transcript;
    const audio = override ? override.audio : recorder.audio;
    if (!text.trim() && !audio) return;
    setProcessingSource(audio ? "voice" : "text");
    const processed = await onProcess(text, audio, turnstileToken);
    // A Turnstile token is single-use: it is spent even when the call fails, so
    // the widget is always re-issued or a retry would be rejected forever.
    if (turnstileSiteKey) {
      setTurnstileToken(undefined);
      setTurnstileAttempt((value) => value + 1);
    }
    if (!processed) return;
    recorder.reset();
    setShowKeyboard(false);
  };

  // Stopping always lands on `ready`, whether the person tapped the button or
  // simply went quiet, so both routes send through this single effect.
  useEffect(() => {
    if (recorder.status !== "ready" || busy) return;
    if (turnstileSiteKey && !turnstileToken) return;
    if (!recorder.transcript.trim() && !recorder.audio) return;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.status]);

  const toggleRecording = async () => {
    if (recording) await recorder.stop();
    else if (!finishing && !busy) await recorder.start();
  };

  return (
    <section className="capture-view" aria-labelledby="capture-title">
      <div className="capture-copy">
        <p className="eyebrow">Assistant vocal personnel</p>
        <h1 id="capture-title">Dites-le. Je m’occupe du reste.</h1>
        <p>Une idée, une note, un document ou un rendez-vous — parlez naturellement.</p>
      </div>

      <div className={`voice-stage ${recording ? "is-recording" : ""} ${finishing ? "is-busy" : ""}`}>
        <div className="voice-orbit orbit-one" aria-hidden="true" />
        <div className="voice-orbit orbit-two" aria-hidden="true" />
        <button
          className="record-button"
          type="button"
          aria-pressed={recording}
          aria-label={recording ? "Arrêter l’enregistrement" : "Démarrer l’enregistrement"}
          disabled={finishing || busy}
          onClick={() => void toggleRecording()}
        >
          {finishing ? (
            <LoaderCircle className="spin" aria-hidden="true" />
          ) : recording ? (
            <Square aria-hidden="true" fill="currentColor" />
          ) : (
            <Mic aria-hidden="true" />
          )}
        </button>
        {recording && <span className="recording-time">{formatDuration(recorder.elapsedSeconds)}</span>}
        {recording && recorder.mode === "audio" && (
          <div className="input-meter" aria-label="Niveau du microphone">
            <span style={{ transform: `scaleX(${Math.max(0.05, recorder.inputLevel)})` }} />
          </div>
        )}
      </div>

      <div className="record-instructions" aria-live="polite">
        <strong>
          {recording
            ? "Je vous écoute…"
            : finishing
              ? "Je finalise…"
              : ready
                ? "C’est prêt"
                : "Touchez pour parler"}
        </strong>
        <span>
          {recording
            ? autoStopOnSilence
              ? "Touchez à nouveau, ou faites simplement une pause"
              : "Touchez à nouveau pour terminer"
            : recorder.mode === "speech"
              ? "Votre appareil transcrit, ParleNote remet au propre"
              : "L’audio est transcrit et remis au propre sur le serveur"}
        </span>
      </div>

      {recording && (
        <div className="live-transcript" aria-live="polite" aria-atomic="true">
          <span>
            <i aria-hidden="true" />
            Texte en direct
            {recorder.silenceCountdown !== undefined && (
              <em className="silence-countdown">J’envoie dans {recorder.silenceCountdown} s</em>
            )}
          </span>
          <p>
            {recorder.mode === "speech"
              ? recorder.transcript || "Parlez, les mots apparaîtront ici…"
              : "Votre voix est bien captée. La transcription complète apparaîtra automatiquement à l’arrêt."}
          </p>
          {previewUnavailable && (
            <small className="live-transcript-warning">{recorder.error}</small>
          )}
        </div>
      )}

      {(finishing || busy) && (
        <div className="processing-card" role="status" aria-live="polite">
          <LoaderCircle className="spin" aria-hidden="true" />
          <div>
            <strong>{processingLabel}</strong>
            <span>Vous pouvez rester ici, ParleNote prépare la réponse.</span>
          </div>
          <i aria-hidden="true" />
        </div>
      )}

      {!busy && (ready || showKeyboard || recorder.status === "error") && (
        <div className="transcript-editor">
          <div className="field-heading">
            <label htmlFor="capture-transcript">
              {ready ? "Relisez avant d’envoyer" : "Écrire à la place"}
            </label>
            {ready && !recorder.transcript && recorder.audio && (
              <span>Transcription serveur en cours après envoi.</span>
            )}
          </div>
          <textarea
            id="capture-transcript"
            value={recorder.transcript}
            onChange={(event) => recorder.setTranscript(event.target.value)}
            rows={ready ? 4 : 6}
            placeholder={
              ready
                ? "La transcription complète sera produite à partir de l’audio."
                : "Écrivez votre demande ici…"
            }
          />
          {recorder.error && recorder.status === "error" && (
            <p className="inline-error" role="alert">
              {recorder.error}
            </p>
          )}
          <div className="editor-actions">
            <button className="button ghost" type="button" onClick={recorder.reset}>
              <RotateCcw size={18} aria-hidden="true" />
              Effacer
            </button>
            <button
              className="button primary"
              type="button"
              disabled={
                busy ||
                finishing ||
                (!recorder.transcript.trim() && !recorder.audio) ||
                (Boolean(turnstileSiteKey) && !turnstileToken)
              }
              onClick={() => void send()}
            >
              {busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
              {busy ? "Je réfléchis…" : "Envoyer"}
            </button>
          </div>
          {turnstileSiteKey && (
            <TurnstileWidget
              key={turnstileAttempt}
              siteKey={turnstileSiteKey}
              onToken={setTurnstileToken}
            />
          )}
        </div>
      )}

      {!ready && !showKeyboard && recorder.status !== "error" && !busy && !finishing && (
        <button className="text-button keyboard-alternative" type="button" onClick={() => setShowKeyboard(true)}>
          <Keyboard size={17} aria-hidden="true" />
          Écrire à la place
        </button>
      )}

      {((messages && messages.length > 0) || busy) && (
        <section className="conversation-panel" aria-label="Conversation">
          <div className="conversation-heading">
            <div>
              <strong>Conversation</strong>
              <span>Le contexte récent est mémorisé</span>
            </div>
            {onToggleSpeech && (
              <button className="icon-button speech-control" type="button" onClick={onToggleSpeech}>
                {speaking ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
                <span className="sr-only">
                  {speaking ? "Arrêter la voix" : "Relire la dernière réponse"}
                </span>
              </button>
            )}
          </div>
          <div className="conversation-stream" aria-live="polite" ref={conversationRef}>
            {messages?.slice(-20).map((message) => (
              <article className={`message-bubble ${message.role}`} key={message.id}>
                <span>
                  {message.role === "assistant" ? "ParleNote" : "Vous"}
                  <time dateTime={message.createdAt}>
                    {new Intl.DateTimeFormat("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit"
                    }).format(new Date(message.createdAt))}
                  </time>
                </span>
                <p>{message.content}</p>
              </article>
            ))}
            {busy && (
              <article className="message-bubble assistant is-thinking" aria-label="ParleNote réfléchit">
                <span>ParleNote</span>
                <p><i /><i /><i /></p>
              </article>
            )}
          </div>
        </section>
      )}

      <details className="privacy-note">
        <summary>Confidentialité et traitement</summary>
        <p>
          Le traitement exige un compte. L’audio nécessaire est envoyé au serveur puis au
          fournisseur d’IA, avec quotas et contrôle anti-abus. Aucune clé secrète n’est
          stockée dans votre navigateur.
        </p>
      </details>
    </section>
  );
}
